import axios from 'axios';
import axiosCancel from 'axios-cancel';
import { notify } from 'legacy-ft-cb';
import {
  redirectToLogin,
  refreshAccessToken,
} from 'legacy-src/components/auth/utils';
import { VITAL_DOMAINS } from 'legacy-src/constants/config';
import log from 'chameleon/ui-stack/utilities/log';

window.axios = axios;

const interceptorConfig = {
  errorInterceptor(err) {
    const { response, config } = err;

    // If the err is a “Cancel” promise
    if (axios.isCancel(err)) {
      return log('Request canceled', err.message);
    }

    if (config && config.url.includes('Report')) {
      return;
    }

    // Use the whitelist to determine if we should display the error message-
    // Otherwise, we display this even if unnecessary (often third-party) requests fail.
    if (!response && VITAL_DOMAINS.find((d) => config.url.includes(d))) {
      notify.error(
        'Error processing backend requests. Please contact the support team.',
      );
      log('error', err);
    }

    if (!response) {
      throw new Error(err);
    }

    if (
      response.status === 400 &&
      response.data &&
      response.data.message === 'Assessment is rotten'
    ) {
      notify.error(
        "This assessment contains a question that is no longer in this subject's library, and cannot ".concat(
          "be assigned. Please create a new assessment using questions still available from the 'Create' tab.",
        ),
      );
    } else if (response.status === 422) {
      redirectToLogin();
    } else if (response.status >= 500) {
      notify.error(
        'Error processing the request. Please, contact the support team.',
      );
    }

    return response;
  },
  tokenInterceptor(config) {
    const { skipOverride, expires: customExpires } = config.headers;
    const expires = skipOverride
      ? customExpires
      : window.localStorage.getItem('expires');
    // if the token is going to expire lets re-issue it pre-emptively
    const expiresValueOf = Number.parseInt(expires, 10);
    if (new Date() > new Date(expiresValueOf - 5000)) {
      return handleUnauthorizedResponse(config);
    }
    return config;
  },
  headerInterceptor(oldConfig) {
    const config = { ...oldConfig };
    delete config.headers.authorization;
    delete config.headers.common.Authorization;
    delete config.headers.common.authorization;

    let token;
    if (config.headers.skipOverride) {
      token = config.headers.accessToken;
    } else {
      token = window.localStorage.getItem('account_access_token');
    }
    if (!!token) {
      config.headers.common.Authorization = `Bearer ${token}`;
    }
    delete config.headers.skipOverride;
    delete config.headers.skipRefresh;
    delete config.headers.accessToken;
    delete config.headers.refreshToken;
    delete config.headers.expires;

    return config;
  },
};

function handleUnauthorizedResponse(originalConfig) {
  const {
    headers: { skipRefresh, skipOverride, accessToken, refreshToken, expires },
  } = originalConfig;

  if (skipRefresh) {
    return originalConfig;
  }

  let refreshTokens;
  if (skipOverride) {
    refreshTokens = {
      accessToken,
      refreshToken,
      expires,
    };
  }

  return refreshAccessToken(refreshTokens).then((response) => {
    const config = { ...originalConfig };
    delete config.headers.Authorization;
    delete config.headers.authorization;
    delete config.headers.common.Authorization;
    delete config.headers.common.authorization;
    const { access_token: newAccessToken } = response.data;
    config.headers.common.Authorization = `Bearer ${newAccessToken}`;
    if (skipOverride) {
      config.headers.accessToken = newAccessToken;
    }
    return config;
  });
}

const bootstrapApi = () => {
  axios.interceptors.request.handlers = [];
  axios.interceptors.response.handlers = [];

  axiosCancel(axios);

  // Attach global API interceptors
  axios.interceptors.response.use(
    undefined,
    interceptorConfig.errorInterceptor,
  );
  axios.interceptors.request.use(interceptorConfig.tokenInterceptor);
  axios.interceptors.request.use(interceptorConfig.headerInterceptor);
};

export default bootstrapApi;
